import { createContext, useContext, useState } from "react";

export const PERSONA_META = {
  logical: {
    color: "#1F5BFF",
    en: { name: "The Analyst", blurb: "You find patterns where others see noise. Paths to explore: data science, engineering, finance, law." },
    hi: { name: "विश्लेषक", blurb: "जहाँ दूसरों को शोर दिखता है, वहाँ आपको पैटर्न दिखते हैं। रास्ते: डेटा साइंस, इंजीनियरिंग, फाइनेंस, कानून।" },
  },
  creative: {
    color: "#7C3AED",
    en: { name: "The Creator", blurb: "You turn imagination into things people can touch. Paths to explore: design, architecture, media, writing." },
    hi: { name: "निर्माता", blurb: "आप कल्पना को ऐसी चीज़ों में बदलते हैं जिन्हें लोग छू सकते हैं। रास्ते: डिज़ाइन, आर्किटेक्चर, मीडिया, लेखन।" },
  },
  social: {
    color: "#E84393",
    en: { name: "The Empath", blurb: "You read rooms and lift people up. Paths to explore: psychology, teaching, medicine, HR." },
    hi: { name: "समवेदी", blurb: "आप माहौल पढ़ लेते हैं और लोगों का हौसला बढ़ाते हैं। रास्ते: मनोविज्ञान, शिक्षण, चिकित्सा, मानव संसाधन।" },
  },
  trailblazer: {
    color: "#F5A524",
    en: { name: "The Trailblazer", blurb: "You organize, decide and deliver. Paths to explore: entrepreneurship, management, public policy." },
    hi: { name: "अग्रणी", blurb: "आप व्यवस्थित करते हैं, फ़ैसला लेते हैं और पूरा करके दिखाते हैं। रास्ते: उद्यमिता, प्रबंधन, सार्वजनिक नीति।" },
  },
  explorer: {
    color: "#3AA64B",
    en: { name: "The Explorer", blurb: "Curiosity is your compass. Paths to explore: research, biotech, environment, space science." },
    hi: { name: "खोजी", blurb: "जिज्ञासा ही आपकी दिशा है। रास्ते: रिसर्च, बायोटेक, पर्यावरण, अंतरिक्ष विज्ञान।" },
  },
};

export const DOMAIN_ORDER = ["logical", "creative", "social", "trailblazer", "explorer"];

export const STR = {
  en: {
    badge: "Psychometric Assessment Portal",
    h1a: "Know yourself.",
    h1b: "Shape your",
    h1c: "tomorrow.",
    sub: "Bright IQ turns five sciences of the mind — aptitude, personality, interest, intelligence and values — into one clear, counselor-reviewed map for your career and studies.",
    cta1: "Start the Full Assessment",
    cta2: "Explore the full assessment",
    stats: [["5", "sciences mapped"], ["40+", "career clusters"], ["15 min", "to first insight"]],
    navCta: "Start the Full Assessment",
    signin: "Sign in",
    dashboard: "Dashboard",
    discovery: "Discovery",
    institutions: "For Institutions",
    links: { manifesto: "The Method", report: "The Report", journey: "The Journey", stories: "Stories", faq: "FAQ" },
    faqTag: "FAQ",
    faqHeadA: "Questions,",
    faqHeadB: "answered honestly.",
    ctaHeadingA: "Your tomorrow is",
    ctaHeadingB: "waiting.",
    ctaSub: (count) => `Join the Bright IQ early-access list${count > 0 ? ` — ${count} curious minds already have` : ""} and be first to take the full assessment.`,
    ctaButton: "Get early access",
    ctaPlaceholder: "you@example.com",
  },
  hi: {
    badge: "साइकोमेट्रिक आकलन पोर्टल",
    h1a: "खुद को जानिए।",
    h1b: "अपना कल",
    h1c: "गढ़िए।",
    sub: "Bright IQ मन के पाँच विज्ञानों — योग्यता, व्यक्तित्व, रुचि, बुद्धि और मूल्यों — को आपके करियर और पढ़ाई के एक स्पष्ट, काउंसलर-समीक्षित नक्शे में बदल देता है।",
    cta1: "पूरा आकलन शुरू करें",
    cta2: "पूरा आकलन देखें",
    stats: [["5", "विज्ञान मैप किए"], ["40+", "करियर क्लस्टर"], ["15 मिनट", "पहली झलक तक"]],
    navCta: "पूरा आकलन शुरू करें",
    signin: "साइन इन",
    dashboard: "डैशबोर्ड",
    discovery: "डिस्कवरी",
    institutions: "संस्थानों के लिए",
    links: { manifesto: "तरीका", report: "रिपोर्ट", journey: "सफ़र", stories: "कहानियाँ", faq: "सवाल-जवाब" },
    faqTag: "सवाल-जवाब",
    faqHeadA: "हर सवाल,",
    faqHeadB: "ईमानदार जवाब।",
    ctaHeadingA: "आपका कल",
    ctaHeadingB: "इंतज़ार कर रहा है।",
    ctaSub: (count) => `Bright IQ अर्ली-एक्सेस सूची से जुड़ें${count > 0 ? ` — ${count} जिज्ञासु मन पहले ही जुड़ चुके हैं` : ""} और पूरा आकलन सबसे पहले आज़माएँ।`,
    ctaButton: "अर्ली एक्सेस पाएँ",
    ctaPlaceholder: "aap@example.com",
  },
};

const LangCtx = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("biq_lang");
      return saved === "hi" ? "hi" : "en";
    } catch {
      return "en";
    }
  });
  const setLang = (l) => {
    try { localStorage.setItem("biq_lang", l); } catch {}
    setLangState(l);
  };
  return <LangCtx.Provider value={{ lang, setLang, t: STR[lang] }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-ink/15 p-0.5 ${className}`} data-testid="lang-toggle">
      {["en", "hi"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 text-xs font-bold rounded-full transition-colors duration-300 ${lang === l ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}
        >
          {l === "en" ? "EN" : "हिं"}
        </button>
      ))}
    </div>
  );
};
